import React, { useEffect, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Typography,
  message,
  Tag
} from 'antd'
import adminService from '../../../services/admin'
import { INSTRUCTOR, ADMIN } from '../../../constants/userRoles'

const { Title } = Typography

const AdminCourses = () => {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 25 })
  const [filters, setFilters] = useState({ q: '', status: undefined })
  const [instructors, setInstructors] = useState([])

  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editingCourse, setEditingCourse] = useState(null)
  const [enrollOpen, setEnrollOpen] = useState(false)
  const [enrollmentsLoading, setEnrollmentsLoading] = useState(false)
  const [enrollments, setEnrollments] = useState([])
  const [enrollCourse, setEnrollCourse] = useState(null)
  const [allUsers, setAllUsers] = useState([])

  const [createForm] = Form.useForm()
  const [editForm] = Form.useForm()
  const [enrollForm] = Form.useForm()

  const load = async (params = {}) => {
    try {
      setLoading(true)
      const res = await adminService.listCourses(params)
      setData(res)
    } catch (e) {
      message.error(e?.response?.data?.message || 'Failed to load courses')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load({ page: 1, limit: 25 })
      ; (async () => {
        try {
          // include instructors and admins as possible "lecturers"
          const [inst, admins] = await Promise.all([
            adminService.listUsers({ role: INSTRUCTOR, limit: 100 }),
            adminService.listUsers({ role: ADMIN, limit: 100 })
          ])
          const merged = [...(inst?.items || []), ...(admins?.items || [])]
          // de-dup by _id
          const map = new Map()
          merged.forEach((u) => map.set(String(u._id), u))
          setInstructors(Array.from(map.values()))
        } catch (_) {
          // ignore - course modals will still work without selection
        }
      })()
      ; (async () => {
        try {
          const res = await adminService.listUsers({ limit: 100 })
          setAllUsers(res?.items || [])
        } catch (_) {
          // ignore
        }
      })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const openEnrollments = async (course) => {
    setEnrollCourse(course)
    setEnrollOpen(true)
    setEnrollmentsLoading(true)
    try {
      const res = await adminService.getCourseEnrollments(course._id || course.id)
      setEnrollments(res || [])
    } catch (e) {
      message.error(e?.response?.data?.message || 'Failed to load enrollments')
    } finally {
      setEnrollmentsLoading(false)
    }
  }

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    {
      title: 'Created by',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (v) => v?.email || v?.name || ''
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => openEnrollments(record)}>
            Manage Enrollments
          </Button>
          <Button
            size="small"
            onClick={() => {
              setEditingCourse(record)
              editForm.setFieldsValue({
                name: record.name,
                description: record.description,
                image: record.image,
                status: record.status,
                createdBy: record.createdBy?._id,
                category: record.category,
                type: record.type
              })
              setEditOpen(true)
            }}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete course?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={async () => {
              try {
                await adminService.deleteCourse(record._id || record.id)
                message.success('Course deleted')
                await load({ ...filters, page: data.page, limit: data.limit })
              } catch (e) {
                message.error(e?.response?.data?.message || 'Failed to delete course')
              }
            }}
          >
            <Button size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  const enrollmentColumns = [
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
      render: (u) => `${u?.name || ''} (${u?.email || ''})`
    },
    {
      title: 'Enrolled As',
      dataIndex: 'enrolledAs',
      key: 'enrolledAs',
      render: (v) => <Tag>{v}</Tag>
    },
    {
      title: 'Change Privilege',
      key: 'change',
      render: (_, record) => (
        <Select
          style={{ width: 160 }}
          value={record.enrolledAs}
          onChange={async (v) => {
            try {
              const res = await adminService.updateCourseEnrollment(
                enrollCourse._id || enrollCourse.id,
                record._id,
                { enrolledAs: v }
              )
              setEnrollments(res || [])
              message.success('Enrollment updated')
            } catch (e) {
              message.error(e?.response?.data?.message || 'Failed to update enrollment')
            }
          }}
          options={[
            { value: 'student', label: 'student' },
            { value: 'instructor', label: 'instructor' },
            { value: 'admin', label: 'admin' }
          ]}
        />
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Popconfirm
          title="Remove this user from course?"
          okText="Remove"
          okButtonProps={{ danger: true }}
          onConfirm={async () => {
            try {
              const res = await adminService.deleteCourseEnrollment(
                enrollCourse._id || enrollCourse.id,
                record._id
              )
              setEnrollments(res || [])
              message.success('Enrollment removed')
            } catch (e) {
              message.error(e?.response?.data?.message || 'Failed to remove enrollment')
            }
          }}
        >
          <Button size="small" danger>
            Remove
          </Button>
        </Popconfirm>
      )
    }
  ]

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Courses
          </Title>
        </Col>
        <Col>
          <Button type="primary" onClick={() => setCreateOpen(true)}>
            Create Course
          </Button>
        </Col>
      </Row>

      <Card style={{ marginBottom: 12 }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} md={16}>
            <Input
              placeholder="Search name/description"
              value={filters.q}
              onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))}
            />
          </Col>
          <Col xs={24} md={6}>
            <Select
              allowClear
              placeholder="Status"
              style={{ width: '100%' }}
              value={filters.status}
              onChange={(v) => setFilters((p) => ({ ...p, status: v }))}
              options={[
                { value: 'published', label: 'published' },
                { value: 'archived', label: 'archived' }
              ]}
            />
          </Col>
          <Col xs={24} md={2}>
            <Button block onClick={() => load({ ...filters, page: 1, limit: data.limit })}>
              Go
            </Button>
          </Col>
        </Row>
      </Card>

      <Table
        rowKey={(r) => r._id || r.id}
        loading={loading}
        columns={columns}
        dataSource={data.items}
        pagination={{
          current: data.page,
          pageSize: data.limit,
          total: data.total,
          showSizeChanger: true,
          onChange: (page, pageSize) => load({ ...filters, page, limit: pageSize })
        }}
      />

      <Modal
        title="Create course"
        visible={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => createForm.submit()}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={async (values) => {
            try {
              await adminService.createCourse(values)
              message.success('Course created')
              setCreateOpen(false)
              createForm.resetFields()
              await load({ ...filters, page: 1, limit: data.limit })
            } catch (e) {
              message.error(e?.response?.data?.message || 'Failed to create course')
            }
          }}
        >
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea />
          </Form.Item>
          <Form.Item name="image" label="Image URL">
            <Input />
          </Form.Item>
          <Form.Item name="createdBy" label="Assign to instructor (lecturer)">
            <Select
              allowClear
              placeholder="Select instructor"
              options={instructors.map((u) => ({
                value: u._id,
                label: `${u.name} (${u.email})`
              }))}
            />
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="published">
            <Select
              options={[
                { value: 'published', label: 'published' },
                { value: 'archived', label: 'archived' }
              ]}
            />
          </Form.Item>
          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: 'Select or enter a category' }]}
          >
            <Select
              mode="tags"
              maxCount={1}
              placeholder="Select or type category"
              options={[
                { value: 'IT', label: 'IT' },
                { value: 'Health', label: 'Health' },
                { value: 'Business', label: 'Business' },
                { value: 'Management', label: 'Management' },
                { value: 'Personal', label: 'Personal Dev' },
                { value: 'Marketing', label: 'Marketing' },
                { value: 'Engineering', label: 'Engineering' },
                { value: 'Teaching', label: 'Teaching' },
                { value: 'Language', label: 'Language' }
              ]}
            />
          </Form.Item>
          <Form.Item name="type" label="Type" initialValue="Certificate" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'Diploma', label: 'Diploma' },
                { value: 'Certificate', label: 'Certificate' },
                { value: 'Skill Path', label: 'Skill Path' },
                { value: 'Corporate Training', label: 'Corporate Training' }
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Edit course"
        visible={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={() => editForm.submit()}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={async (values) => {
            try {
              await adminService.updateCourse(editingCourse._id || editingCourse.id, values)
              message.success('Course updated')
              setEditOpen(false)
              setEditingCourse(null)
              await load({ ...filters, page: data.page, limit: data.limit })
            } catch (e) {
              message.error(e?.response?.data?.message || 'Failed to update course')
            }
          }}
        >
          <Form.Item name="name" label="Name">
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea />
          </Form.Item>
          <Form.Item name="image" label="Image URL">
            <Input />
          </Form.Item>
          <Form.Item name="createdBy" label="Assign to instructor (lecturer)">
            <Select
              allowClear
              placeholder="Select instructor"
              options={instructors.map((u) => ({
                value: u._id,
                label: `${u.name} (${u.email})`
              }))}
            />
          </Form.Item>
          <Form.Item name="status" label="Status">
            <Select
              options={[
                { value: 'published', label: 'published' },
                { value: 'archived', label: 'archived' }
              ]}
            />
          </Form.Item>
          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: 'Select or enter a category' }]}
          >
            <Select
              mode="tags"
              maxCount={1}
              placeholder="Select or type category"
              options={[
                { value: 'IT', label: 'IT' },
                { value: 'Health', label: 'Health' },
                { value: 'Business', label: 'Business' },
                { value: 'Management', label: 'Management' },
                { value: 'Personal', label: 'Personal Dev' },
                { value: 'Marketing', label: 'Marketing' },
                { value: 'Engineering', label: 'Engineering' },
                { value: 'Teaching', label: 'Teaching' },
                { value: 'Language', label: 'Language' }
              ]}
            />
          </Form.Item>
          <Form.Item name="type" label="Type" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'Diploma', label: 'Diploma' },
                { value: 'Certificate', label: 'Certificate' },
                { value: 'Skill Path', label: 'Skill Path' },
                { value: 'Corporate Training', label: 'Corporate Training' }
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Manage enrollments${enrollCourse ? ` — ${enrollCourse.name}` : ''}`}
        visible={enrollOpen}
        onCancel={() => {
          setEnrollOpen(false)
          setEnrollCourse(null)
          setEnrollments([])
          enrollForm.resetFields()
        }}
        footer={null}
        width={1000}
      >
        <Card style={{ marginBottom: 12 }}>
          <Form
            form={enrollForm}
            layout="inline"
            onFinish={async (values) => {
              try {
                const res = await adminService.addCourseEnrollment(
                  enrollCourse._id || enrollCourse.id,
                  values
                )
                setEnrollments(res || [])
                message.success('User enrolled')
                enrollForm.resetFields()
              } catch (e) {
                message.error(e?.response?.data?.message || 'Failed to enroll user')
              }
            }}
          >
            <Form.Item name="userId" rules={[{ required: true, message: 'Select a user' }]}>
              <Select
                showSearch
                placeholder="Select user"
                style={{ width: 420 }}
                optionFilterProp="label"
                options={allUsers.map((u) => ({
                  value: u._id,
                  label: `${u.name} (${u.email}) — ${u.role}`
                }))}
              />
            </Form.Item>
            <Form.Item
              name="enrolledAs"
              rules={[{ required: true, message: 'Select privilege' }]}
              initialValue="student"
            >
              <Select
                style={{ width: 180 }}
                options={[
                  { value: 'student', label: 'student' },
                  { value: 'instructor', label: 'instructor' },
                  { value: 'admin', label: 'admin' }
                ]}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" disabled={!enrollCourse}>
                Add
              </Button>
            </Form.Item>
          </Form>
        </Card>

        <Table
          rowKey={(r) => r._id}
          loading={enrollmentsLoading}
          columns={enrollmentColumns}
          dataSource={enrollments}
          pagination={false}
        />
      </Modal>
    </>
  )
}

export default AdminCourses

